/**
 * Calendar maths for the range picker in "Where and when". Dates are ISO
 * calendar dates (YYYY-MM-DD) handled as UTC days, like the rest of
 * almaFlow/dates.ts, so no timezone or DST shift can move them.
 */
import { addDays, daysBetween, isIsoDate } from './dates';

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO date for a year, month (0-11) and day. */
export function isoOf(year: number, month: number, day: number): string {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

export interface YearMonth {
  year: number;
  /** 0-11. */
  month: number;
}

export function monthOf(iso: string): YearMonth {
  const [y, m] = iso.split('-').map(Number);
  return { year: y, month: m - 1 };
}

export function addMonths(ym: YearMonth, by: number): YearMonth {
  const index = ym.year * 12 + ym.month + by;
  return { year: Math.floor(index / 12), month: ((index % 12) + 12) % 12 };
}

/** Negative when `a` is before `b`. */
export function compareMonths(a: YearMonth, b: YearMonth): number {
  return a.year * 12 + a.month - (b.year * 12 + b.month);
}

/** "October 2026". */
export function monthLabel(ym: YearMonth): string {
  return new Date(Date.UTC(ym.year, ym.month, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** "Tuesday, October 14", for screen readers. */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/**
 * The weeks of a month, Monday first, as ISO dates; days outside the month are
 * null so the grid lines up.
 */
export function monthGrid(ym: YearMonth): (string | null)[][] {
  const first = new Date(Date.UTC(ym.year, ym.month, 1));
  const lead = (first.getUTCDay() + 6) % 7; // Monday = 0
  const length = new Date(Date.UTC(ym.year, ym.month + 1, 0)).getUTCDate();
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length }, (_, i) => isoOf(ym.year, ym.month, i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
}

export interface PickedRange {
  from: string;
  to: string;
}

/**
 * What a tap on a day does. With no first day yet, or a finished range, the tap
 * starts a new range. With only a first day: a later day finishes the range,
 * the same day makes it a one-day stay, and an earlier day moves the start.
 */
export function applyRangeTap(current: { from: string; to: string }, tapped: string): PickedRange {
  const hasFrom = isIsoDate(current.from);
  const hasTo = isIsoDate(current.to);
  if (!hasFrom || hasTo) return { from: tapped, to: '' };
  if (tapped < current.from) return { from: tapped, to: '' };
  return { from: current.from, to: tapped };
}

export const isRangeComplete = (r: { from: string; to: string }) => isIsoDate(r.from) && isIsoDate(r.to);

/** Days in a finished range, both ends counted. */
export function rangeLength(r: PickedRange): number {
  return daysBetween(r.from, r.to) + 1;
}

/** Whether a day is inside a range (ends included). */
export function inRange(iso: string, r: { from: string; to: string }): boolean {
  return isIsoDate(r.from) && isIsoDate(r.to) && iso >= r.from && iso <= r.to;
}

/** The earliest day a city's range can start: the day after the previous city's last day, or tomorrow. */
export function earliestStart(today: string, previousTo?: string): string {
  return isIsoDate(previousTo) ? addDays(previousTo, 1) : addDays(today, 1);
}
